import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'vi-ant-design:dashboard-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.analysis'),
          noCache: true,
          affix: true
        }
      }
    ]
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  //运营管理
  {
    path: '/operation-manage',
    component: Layout,
    redirect: '/operation-manage/module-manage',
    name: 'OperationManage',
    meta: {
      title: '运营管理',
      icon: 'vi-ep:management',
      alwaysShow: true
    },
    children: [
      {
        path: 'module-manage',
        component: () => import('@/views/OperationManage/ModuleManage/index.vue'),
        name: 'ModuleManage',
        meta: {
          title: '模块管理',
          noCache: true
        }
      },
      {
        path: 'content-manage',
        component: () => import('@/views/OperationManage/ContentManage/index.vue'),
        name: 'ContentManage',
        meta: {
          title: '模块配置',
          noCache: true,
          hidden: true
        }
      },
      {
        path: 'sensitive-word-manage',
        component: () => import('@/views/OperationManage/SensitiveWordManage/index.vue'),
        name: 'SensitiveWordManage',
        meta: {
          title: '敏感词配置',
          noCache: true
        }
      },
      {
        path: 'report-manage',
        component: () => import('@/views/OperationManage/ReportManage/index.vue'),
        name: 'ReportManage',
        meta: {
          title: '举报管理',
          noCache: true
        }
      },
      {
        path: 'emoji-set-manage',
        component: () => import('@/views/OperationManage/EmojiSetManage/index.vue'),
        name: 'EmojiSetManage',
        meta: {
          title: '套图管理',
          noCache: true
        }
      },
      {
        path: 'emoji-manage/:id',
        component: () => import('@/views/OperationManage/EmojiManage/index.vue'),
        name: 'EmojiManage',
        meta: {
          title: '表情管理',
          noCache: true,
          hidden: true
        }
      },
      {
        path: 'channel-manage',
        component: () => import('@/views/OperationManage/ChannelManage/index.vue'),
        name: 'ChannelManage',
        meta: {
          title: '渠道管理',
          noCache: true
        }
      },
      {
        path: 'hot-search',
        component: () => import('@/views/OperationManage/HotSearch/HotSearch.vue'),
        name: 'HotSearch',
        meta: {
          title: '热门搜索',
          noCache: true
        }
      },
      {
        path: 'strong-suit',
        component: () => import('@/views/OperationManage/HotSearch/StrongSuit.vue'),
        name: 'StrongSuit',
        meta: {
          title: '擅长领域',
          noCache: true
        }
      },
      {
        path: 'banner-manage',
        component: () => import('@/views/OperationManage/BannerManage/index.vue'),
        name: 'BannerManage',
        meta: {
          title: '焦点图配置',
          noCache: true
        }
      },
      {
        path: 'ranking-manage',
        component: () => import('@/views/OperationManage/RankingManage/index.vue'),
        name: 'RankingManage',
        meta: {
          title: '榜单配置',
          noCache: true
        }
      }
    ]
  },
  // 赛事管理
  {
    path: '/match-manage',
    component: Layout,
    redirect: '/match-manage/jczq',
    name: 'MatchManage',
    meta: {
      title: '赛事管理',
      icon: 'vi-mdi:soccer',
      alwaysShow: true
    },
    children: [
      {
        path: 'jczq',
        component: () => import('@/views/MatchManage/Jczq.vue'),
        name: 'Jczq',
        meta: {
          title: '竞彩足球',
          noCache: true
        }
      },
      {
        path: 'jczq-lottery',
        component: () => import('@/views/MatchManage/JczqLottery.vue'),
        name: 'JczqLottery',
        meta: {
          title: '竞足开奖',
          noCache: true
        }
      },
      {
        path: 'jclq',
        component: () => import('@/views/MatchManage/Jclq.vue'),
        name: 'Jclq',
        meta: {
          title: '竞彩篮球',
          noCache: true
        }
      },
      {
        path: 'jclq-lottery',
        component: () => import('@/views/MatchManage/JclqLottery.vue'),
        name: 'JclqLottery',
        meta: {
          title: '竞篮开奖',
          noCache: true
        }
      },
      {
        path: 'bjdc',
        component: () => import('@/views/MatchManage/Bjdc.vue'),
        name: 'Bjdc',
        meta: {
          title: '北京单场',
          noCache: true
        }
      },
      {
        path: 'bjdc-lottery',
        component: () => import('@/views/MatchManage/BjdcLottery.vue'),
        name: 'BjdcLottery',
        meta: {
          title: '北单开奖',
          noCache: true
        }
      },
      {
        path: 'lottery-period',
        component: () => import('@/views/MatchManage/LotteryPeriod.vue'),
        name: 'LotteryPeriod',
        meta: {
          title: '彩期管理',
          noCache: true
        }
      }
    ]
  },
  // 叭叭管理
  {
    path: '/baba-manage',
    component: Layout,
    redirect: '/baba-manage/pusher-manage/pusher-list',
    name: 'BabaManage',
    meta: {
      title: '叭叭管理',
      icon: 'vi-mdi:bullhorn',
      alwaysShow: true
    },
    children: [
      {
        path: 'post-manage',
        component: () => import('@/views/BaBaManage/PostManage/index.vue'),
        name: 'PostManage',
        meta: {
          title: '帖子管理',
          noCache: true
        }
      },
      {
        path: 'comment-manage',
        component: () => import('@/views/BaBaManage/CommentManage/index.vue'),
        name: 'CommentManage',
        meta: {
          title: '评论管理',
          noCache: true
        }
      },
      {
        path: 'reply-manage',
        component: () => import('@/views/BaBaManage/ReplyManage/index.vue'),
        name: 'ReplyManage',
        meta: {
          title: '回复管理',
          noCache: true
        }
      },
      {
        path: 'plan-manage',
        component: () => import('@/views/BaBaManage/PlanManage/index.vue'),
        name: 'PlanManage',
        meta: {
          title: '方案管理',
          noCache: true
        }
      },
      {
        path: 'pusher-manage',
        component: getParentLayout(),
        redirect: '/baba-manage/pusher-manage/pusher-list',
        name: 'PusherManage',
        meta: {
          title: '推单师管理',
          alwaysShow: true
        },
        children: [
          {
            path: 'pusher-list',
            component: () => import('@/views/BaBaManage/PusherManage/PusherList.vue'),
            name: 'PusherList',
            meta: {
              title: '推单师列表',
              noCache: true
            }
          },
          {
            path: 'promotion',
            component: () => import('@/views/BaBaManage/PusherManage/Promotion.vue'),
            name: 'PusherPromotion',
            meta: {
              title: '推单师升降级条件',
              noCache: true
            }
          },
          {
            path: 'comprehensive-value',
            component: () => import('@/views/BaBaManage/PusherManage/ComprehensiveValue.vue'),
            name: 'PusherComprehensiveValue',
            meta: {
              title: '排序综合值配置',
              noCache: true
            }
          }
        ]
      }
    ]
  },
  // 会员管理
  {
    path: '/userManage',
    component: Layout,
    name: 'UserManage',
    meta: {
      title: '会员管理',
      icon: 'vi-mdi:account-group',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/UserManage/List.vue'),
        name: 'UserList',
        meta: {
          title: '用户列表'
        }
      },
      {
        path: 'account-flow',
        component: () => import('@/views/UserManage/AccountFlow.vue'),
        name: 'AccountFlow',
        meta: {
          title: '账户流水'
        }
      },
      {
        path: 'withdraw-apply',
        component: () => import('@/views/UserManage/WithdrawApply.vue'),
        name: 'WithdrawApply',
        meta: {
          title: '提现申请'
        }
      }
    ]
  },
  // 订单管理
  {
    path: '/orderManage',
    component: Layout,
    name: 'OrderManage',
    meta: {
      title: '订单管理',
      icon: 'vi-mdi:clipboard-list',
      alwaysShow: true
    },
    children: [
      {
        path: 'push-record',
        component: () => import('@/views/OrderManage/PushRecord.vue'),
        name: 'PushRecord',
        meta: {
          title: '购买推单记录'
        }
      },
      {
        path: 'push-fail-record',
        component: () => import('@/views/OrderManage/PushFailRecord.vue'),
        name: 'PushFailRecord',
        meta: {
          title: '推单回扣失败记录'
        }
      },
      {
        path: 'tianying-order',
        component: () => import('@/views/OrderManage/TianyingOrder.vue'),
        name: 'TianyingOrder',
        meta: {
          title: '天盈订单'
        }
      }
    ]
  },
  // 邀请管理
  {
    path: '/invite-manage',
    component: Layout,
    name: 'InviteManage',
    meta: {
      title: '邀请管理',
      icon: 'vi-mdi:account-multiple-plus',
      alwaysShow: true
    },
    children: [
      {
        path: 'invite-record',
        component: () => import('@/views/InviteManage/InviteRecord.vue'),
        name: 'InviteRecord',
        meta: {
          title: '邀请记录'
        }
      }
    ]
  },
  // 财务管理
  {
    path: '/finance-manage',
    component: Layout,
    name: 'FinanceManage',
    redirect: '/finance-manage/channel-settlement',
    meta: {
      title: '财务管理',
      icon: 'vi-mdi:finance',
      alwaysShow: true
    },
    children: [
      {
        path: 'channel-settlement',
        component: () => import('@/views/FinanceManage/ChannelSettlement/index.vue'),
        name: 'ChannelSettlement',
        meta: {
          title: '推单渠道结算',
          noCache: true
        }
      },
      {
        path: 'pusher-settlement',
        component: () => import('@/views/FinanceManage/PusherSettlement/index.vue'),
        name: 'PusherSettlement',
        meta: {
          title: '推单师结算',
          noCache: true
        }
      }
    ]
  },
  // 推送管理
  {
    path: '/pushManage',
    component: Layout,
    name: 'PushManage',
    meta: {
      title: '推送管理',
      icon: 'vi-ep:bell-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'history-message',
        component: () => import('@/views/PushManage/HistoryMessage.vue'),
        name: 'HistoryMessage',
        meta: {
          title: '历史消息'
        }
      },
      {
        path: 'message-template',
        component: () => import('@/views/PushManage/MessageTemplate.vue'),
        name: 'MessageTemplate',
        meta: {
          title: '消息模版'
        }
      },
      {
        path: 'user-setting',
        component: () => import('@/views/PushManage/UserSetting.vue'),
        name: 'UserSetting',
        meta: {
          title: '用户设置'
        }
      }
    ]
  },
  //文章管理
  {
    path: '/articleManage',
    component: Layout,
    name: 'ArticleManage',
    meta: {
      title: '文章管理',
      icon: 'vi-mdi:file-document',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/ArticleManage/List.vue'),
        name: 'ArticleList',
        meta: {
          title: '文章列表'
        }
      }
    ]
  },
  //资料审核
  {
    path: '/resourceAudit',
    component: Layout,
    name: 'ResourceAudit',
    meta: {
      title: '资料审核',
      icon: 'vi-material-symbols:fact-check',
      alwaysShow: true
    },
    children: [
      {
        path: 'userAvatar',
        component: () => import('@/views/ResouceAudit/Avatar.vue'),
        name: 'UserAvatar',
        meta: {
          title: '用户头像审核'
        }
      },
      {
        path: 'userNickName',
        component: () => import('@/views/ResouceAudit/UserNickName.vue'),
        name: 'UserNickName',
        meta: {
          title: '用户昵称审核'
        }
      },
      {
        path: 'userDescription',
        component: () => import('@/views/ResouceAudit/UserDescription.vue'),
        name: 'UserDescription',
        meta: {
          title: '用户简介审核'
        }
      }
    ]
  },
  //版本管理
  {
    path: '/versionManage',
    component: Layout,
    name: 'VersionManage',
    meta: {
      title: '版本管理',
      icon: 'vi-carbon:version',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/VersionManage/List.vue'),
        name: 'VersionList',
        meta: {
          title: '版本列表'
        }
      }
    ]
  },
  //充值管理
  {
    path: '/rechargeManage',
    component: Layout,
    name: 'RechargeManage',
    meta: {
      title: '充值管理',
      icon: 'vi-mdi:cash-multiple',
      alwaysShow: true
    },
    children: [
      {
        path: 'setting',
        component: () => import('@/views/RechargeManage/Setting.vue'),
        name: 'RechargeSetting',
        meta: {
          title: '充值设置'
        }
      },
      {
        path: 'list',
        component: () => import('@/views/RechargeManage/List.vue'),
        name: 'RechargeList',
        meta: {
          title: '充值记录'
        }
      }
    ]
  },
  //协议管理
  {
    path: '/agreementManage',
    component: Layout,
    name: 'AgreementManage',
    meta: {
      title: '协议管理',
      icon: 'vi-mdi:file-document-outline',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/AgreementManage/AgreementList.vue'),
        name: 'AgreementList',
        meta: {
          title: '协议列表'
        }
      }
    ]
  },
  {
    path: '/error',
    component: Layout,
    redirect: '/error/404',
    name: 'Error',
    meta: {
      title: t('router.errorPage'),
      icon: 'vi-ci:error',
      hidden: true,
      alwaysShow: false
    },
    children: [
      {
        path: '404-demo',
        component: () => import('@/views/Error/404.vue'),
        name: '404Demo',
        meta: {
          title: '404'
        }
      },
      {
        path: '403-demo',
        component: () => import('@/views/Error/403.vue'),
        name: '403Demo',
        meta: {
          title: '403'
        }
      },
      {
        path: '500-demo',
        component: () => import('@/views/Error/500.vue'),
        name: '500Demo',
        meta: {
          title: '500'
        }
      }
    ]
  },
  {
    path: '/authorization',
    component: Layout,
    redirect: '/authorization/user',
    name: 'Authorization',
    meta: {
      title: t('router.authorization'),
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true
    },
    children: [
      {
        path: 'adminLog',
        component: () => import('@/views/Authorization/AdminLog/AdminLog.vue'),
        name: 'AdminLog',
        meta: {
          title: t('router.adminLog')
        }
      },
      {
        path: 'admin',
        component: () => import('@/views/Authorization/Admin/Admin.vue'),
        name: 'Admin',
        meta: {
          title: t('router.admin')
        }
      },
      {
        path: 'menu',
        component: () => import('@/views/Authorization/Menu/Menu.vue'),
        name: 'Menu',
        meta: {
          title: t('router.menuManagement')
          // noCache: true
        }
      },
      {
        path: 'role',
        component: () => import('@/views/Authorization/Role/Role.vue'),
        name: 'Role',
        meta: {
          title: t('router.role')
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
